import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Camera, Check, Trash2, Loader2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND}/api`;

/**
 * Mobile UX v2 — Phase 6: rapid multi-photo capture flow.
 *
 * Two source modes (set via the `source` prop, default "camera"):
 *   - "camera": auto-opens device camera, sequential burst capture
 *   - "gallery": auto-opens gallery picker with `multiple` so the user can
 *     hand-pick several images at once. Useful for importing photos shot
 *     earlier (e.g. with a DSLR or another app).
 *
 * In both modes captions are intentionally NOT collected — the field worker
 * can edit individual photos later from the L3 Photos view to add a caption
 * or mark a photo as a finding/follow-up item.
 */
export default function MultiCaptureSheet({ report, unit, editToken, source = "camera", onClose, onCompleted }) {
  const isEditMode = !!editToken;
  const photoUrl = isEditMode
    ? `${API_BASE}/reports/edit/${editToken}/units/${unit.id}/photos`
    : `/reports/${report.report_id}/units/${unit.id}/photos`;
  const post = (url, body, config) => (isEditMode ? axios.post(url, body, config) : api.post(url, body, config));

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const isGallery = source === "gallery";
  const [items, setItems] = useState([]); // [{ id, file, previewUrl, compressPromise, status, error }]
  const [phase, setPhase] = useState("opening"); // opening | gallery | uploading | done
  const [uploaded, setUploaded] = useState(0);
  const itemIdRef = useRef(0);

  // Auto-open the appropriate input on mount. Same cancel/fallback strategy
  // as PhotoCaptureSheet.
  useEffect(() => {
    const input = isGallery ? galleryRef.current : cameraRef.current;
    let cancelled = false;
    const raf = requestAnimationFrame(() => { if (!cancelled && input) input.click(); });
    const onCancel = () => { if (!cancelled) setPhase("gallery"); };
    input?.addEventListener("cancel", onCancel);
    const fallback = setTimeout(() => {
      setPhase((p) => (p === "opening" ? "gallery" : p));
    }, 1800);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
      input?.removeEventListener("cancel", onCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => items.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const additions = files.map((f) => {
        const id = ++itemIdRef.current;
        const previewUrl = URL.createObjectURL(f);
        const compressPromise = compressImage(f).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("multi-capture compress failed, will retry at upload:", err);
          return null;
        });
        return { id, file: f, previewUrl, compressPromise, status: "pending", error: null };
      });
      setItems((prev) => [...prev, ...additions]);
      setPhase("gallery");
    }
    e.target.value = "";
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const next = prev.filter((it) => {
        if (it.id === id && it.previewUrl) URL.revokeObjectURL(it.previewUrl);
        return it.id !== id;
      });
      return next;
    });
  };

  const takeAnother = () => {
    setPhase("opening");
    // Re-trigger the appropriate input on next paint
    requestAnimationFrame(() => (isGallery ? galleryRef.current : cameraRef.current)?.click());
    // Fallback in case the picker doesn't open (e.g. user denied permission)
    setTimeout(() => setPhase((p) => (p === "opening" ? "gallery" : p)), 1800);
  };

  const uploadAll = async () => {
    if (items.length === 0) return;
    setPhase("uploading");
    setUploaded(0);

    // Sequential upload — keeps bandwidth predictable on mobile. Could go to
    // limited concurrency later if benchmarks justify it.
    const updated = [...items];
    let okCount = 0;
    let errCount = 0;
    let lastReport = null;
    for (let i = 0; i < updated.length; i++) {
      const it = updated[i];
      if (it.status === "done") { okCount++; continue; }
      try {
        let compressed = it.compressPromise ? await it.compressPromise : null;
        if (!compressed) compressed = await compressImage(it.file);
        const fd = new FormData();
        fd.append("file", compressed, it.file.name || `photo-${it.id}.jpg`);
        fd.append("caption", "");
        fd.append("is_finding", "false");
        const { data } = await post(photoUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
        updated[i] = { ...it, status: "done" };
        okCount++;
        setUploaded((n) => n + 1);
        if (data?.report) lastReport = data.report;
      } catch (err) {
        updated[i] = { ...it, status: "error", error: err?.response?.data?.detail || err?.message || "Lataus epäonnistui" };
        errCount++;
      }
      setItems([...updated]);
    }

    if (errCount === 0) {
      // Ei success-toastia — jää turhaan häiritsemään mobiililla.
      if (lastReport) onCompleted?.(lastReport, okCount);
      setTimeout(() => onClose(), 250);
    } else if (okCount > 0) {
      if (lastReport) onCompleted?.(lastReport, okCount);
      toast.warning(`${errCount} kuvaa epäonnistui — yritä uudelleen`);
      setPhase("gallery");
      // Keep failed items so user can retry
      setItems(updated.filter((it) => it.status !== "done"));
    } else {
      toast.error(`Lataus epäonnistui — yritä uudelleen`);
      setPhase("gallery");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="multi-capture-sheet">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-2xl max-h-[95vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-600">
              {isGallery ? "Valitse galleriasta" : "Lisää useita kuvia"}
            </div>
            <div className="font-display text-base font-semibold text-slate-900 truncate">{unit.label}</div>
          </div>
          <button onClick={onClose} disabled={phase === "uploading"} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-40" data-testid="multi-capture-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* OPENING — show clean loader while the OS picker opens */}
        {phase === "opening" && items.length === 0 && (
          <div className="p-10 flex flex-col items-center justify-center gap-3" data-testid="multi-capture-opening">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center animate-pulse">
              {isGallery ? <ImageIcon className="h-6 w-6 text-emerald-700" /> : <Camera className="h-6 w-6 text-emerald-700" />}
            </div>
            <div className="text-sm font-semibold text-slate-700">
              {isGallery ? "Avataan galleriaa…" : "Avataan kameraa…"}
            </div>
          </div>
        )}

        {/* GALLERY — thumbnails + Ota lisää / Valitse lisää */}
        {phase === "gallery" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                {isGallery
                  ? "Ei kuvia vielä. Paina alta avataksesi gallerian."
                  : "Ei kuvia vielä. Paina alta avataksesi kameran."}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {items.map((it) => (
                  <div key={it.id} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200" data-testid={`multi-capture-thumb-${it.id}`}>
                    <img src={it.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeItem(it.id)}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-rose-600"
                      data-testid={`multi-capture-remove-${it.id}`}
                      aria-label="Poista kuva"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={takeAnother}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 text-emerald-700 font-semibold hover:bg-emerald-50"
              data-testid="multi-capture-take-another"
            >
              {isGallery ? <ImageIcon className="h-5 w-5" /> : <Camera className="h-5 w-5" />}
              {items.length === 0
                ? (isGallery ? "Avaa galleria" : "Avaa kamera")
                : (isGallery ? "Valitse lisää" : "Ota lisää")}
            </button>
          </div>
        )}

        {/* UPLOADING — progress */}
        {phase === "uploading" && (
          <div className="p-8 flex flex-col items-center justify-center gap-3" data-testid="multi-capture-uploading">
            <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
            <div className="font-display text-base font-semibold text-slate-900">Ladataan kuvia…</div>
            <div className="text-sm text-slate-500">{uploaded}/{items.length}</div>
          </div>
        )}

        {/* Footer: Valmis button — only when there are items and not uploading */}
        {phase === "gallery" && items.length > 0 && (
          <div className="border-t border-slate-100 p-3 bg-white">
            <button
              type="button"
              onClick={uploadAll}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
              data-testid="multi-capture-finish"
            >
              <Check className="h-5 w-5" /> Valmis ({items.length})
            </button>
          </div>
        )}

        {/* Hidden camera input — used when source="camera" */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={handleFile}
          data-testid="multi-capture-camera-input"
        />
        {/* Hidden gallery input — multi-select for source="gallery" */}
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFile}
          data-testid="multi-capture-gallery-input"
        />
      </div>
    </div>
  );
}
